import { pageBgColor } from "@/utils/theme";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import {
	ArrowBackRounded,
	ArrowForwardRounded,
	TodayRounded,
} from "@mui/icons-material";
import {
	Box,
	CircularProgress,
	IconButton,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import moment from "moment-timezone";
import { SURFACE } from "./constants/colors";
import { useProductionDateIncentiveQuery } from "./hooks/queries/useProductionDateIncentiveQuery";
import { DailySummaryGrid } from "./dashboard/components/DailySummaryGrid";
import { CrewIncentivesPivot } from "./dashboard/components/CrewIncentivesPivot";
import { IncentiveTanksPanel } from "./dashboard/components/IncentiveTanksPanel";
import { INCENTIVE_ROTATION_STATUS } from "./models/WeeklyIncentiveData";

const getInitialProductionDate = (): moment.Moment => moment().startOf("day");

type IncentivePageProps = {
	assetId: number;
};

export const IncentivePage = ({ assetId }: IncentivePageProps) => {
	const [error, setError] = useState("");
	const [productionDate, setProductionDate] = useState<moment.Moment>(
		getInitialProductionDate,
	);

	const {
		data,
		isLoading,
		isFetching,
		error: queryError,
	} = useProductionDateIncentiveQuery(productionDate, assetId);

	useEffect(() => {
		if (queryError) setError(queryError.message);
	}, [queryError]);

	const handleProductionDateChange = (newValue: moment.Moment | null) => {
		if (!newValue) return;
		setProductionDate(newValue.clone().startOf("day"));
	};

	const handleRotationNavigation = (anchor: string | null) => {
		if (!anchor) return;
		setProductionDate(
			moment(anchor.substring(0, 10), "YYYY-MM-DD").startOf("day"),
		);
	};
	const handleCurrentRotationNavigation = () => {
		setProductionDate(moment().startOf("day"));
	};

	const navigation = data?.rotationNavigation;
	const selectedProductionDate = productionDate.format("YYYY-MM-DD");
	const navigationMatchesSelection =
		navigation?.selectedProductionDate.substring(0, 10) ===
		selectedProductionDate;
	const pickerProductionDate =
		navigationMatchesSelection &&
		navigation.status === INCENTIVE_ROTATION_STATUS.AVAILABLE &&
		navigation.currentBlockStart
			? moment(
					navigation.currentBlockStart.substring(0, 10),
					"YYYY-MM-DD",
				).startOf("day")
			: productionDate;
	const currentProductionDate = moment().format("YYYY-MM-DD");
	const currentBlockStart = navigation?.currentBlockStart?.substring(0, 10);
	const currentBlockEnd = data?.rotationMeta?.currentBlockEnd.substring(0, 10);
	const isCurrentRotation =
		navigationMatchesSelection &&
		currentBlockStart != null &&
		currentBlockEnd != null &&
		currentProductionDate >= currentBlockStart &&
		currentProductionDate <= currentBlockEnd;
	const isQueryLoading = isLoading || isFetching;
	const showDashboard =
		navigationMatchesSelection &&
		navigation.status === INCENTIVE_ROTATION_STATUS.AVAILABLE;
	const statusMessage =
		navigationMatchesSelection &&
		navigation.status === INCENTIVE_ROTATION_STATUS.NO_ROTATION
			? "No crew rotation available for this date"
			: navigationMatchesSelection &&
					navigation.status === INCENTIVE_ROTATION_STATUS.INSUFFICIENT_DATA
				? "Not enough crew rotation data for this date"
				: null;

	return (
		<>
			<HelmetDexteel title="Incentive Dashboard" />
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 64px)",
					backgroundColor: pageBgColor,
					p: "10px 20px",
					overflow: "hidden",
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column", mb: "10px" }}>
					<Typography
						variant="h5"
						sx={{ fontWeight: 600, mb: 1.5, userSelect: "none" }}
					>
						Incentive Dashboard
					</Typography>

					<Paper
						elevation={2}
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-start",
							gap: 2,
							px: 1.5,
							py: 0.75,
						}}
					>
						<Tooltip title="Previous Crew Rotation">
							<span>
								<IconButton
									color="primary"
									aria-label="Previous Crew Rotation"
									disabled={
										isQueryLoading ||
										!navigationMatchesSelection ||
										!navigation.canNavigatePrevious
									}
									onClick={() =>
										handleRotationNavigation(navigation?.previousAnchor ?? null)
									}
								>
									<ArrowBackRounded />
								</IconButton>
							</span>
						</Tooltip>
						<DatePicker
							label="Production Date"
							format="MM/DD/YYYY"
							value={pickerProductionDate}
							onChange={handleProductionDateChange}
							slotProps={{
								textField: { size: "small", sx: { width: 180 } },
							}}
						/>
						<Tooltip title="Next Crew Rotation">
							<span>
								<IconButton
									color="primary"
									aria-label="Next Crew Rotation"
									disabled={
										isQueryLoading ||
										!navigationMatchesSelection ||
										!navigation.canNavigateNext
									}
									onClick={() =>
										handleRotationNavigation(navigation?.nextAnchor ?? null)
									}
								>
									<ArrowForwardRounded />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Current Crew Rotation">
							<span>
								<IconButton
									color="primary"
									aria-label="Current Crew Rotation"
									disabled={isQueryLoading || isCurrentRotation}
									onClick={handleCurrentRotationNavigation}
								>
									<TodayRounded />
								</IconButton>
							</span>
						</Tooltip>
					</Paper>
				</Box>

				<Box
					sx={{
						display: "flex",
						gap: 1,
						flex: 1,
						minHeight: 0,
						alignItems: "stretch",
						position: "relative",
					}}
				>
					{isLoading && (
						<Box
							sx={{
								position: "absolute",
								inset: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: SURFACE.overlay,
								zIndex: 10,
							}}
						>
							<CircularProgress />
						</Box>
					)}
					{statusMessage && !isLoading && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
							}}
						>
							<Typography align="center">{statusMessage}</Typography>
						</Box>
					)}
					{showDashboard && (
						<>
							{/* Left column: Daily Summary */}
							<Box sx={{ flex: 2, minWidth: 0 }}>
								<DailySummaryGrid
									dailyRows={data?.dailyRows ?? []}
									dayTotals={data?.dayTotals ?? []}
									thresholds={data?.thresholds}
								/>
							</Box>

							{/* Right column: Crew Pivot + Tanks */}
							<Box
								sx={{
									flex: 1,
									minWidth: 0,
									display: "flex",
									flexDirection: "column",
									gap: 1,
								}}
							>
								<CrewIncentivesPivot
									crewPivot={data?.crewPivot ?? []}
									thresholds={data?.thresholds}
								/>
								<IncentiveTanksPanel
									tanks={data?.tanks ?? []}
									thresholds={data?.thresholds}
								/>
							</Box>
						</>
					)}
				</Box>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
